// import * as Sentry from '@sentry/browser';
import { Bugfender } from '@bugfender/sdk';
import uuidv4 from 'uuid/v4';
import * as user from '../api/users';
import * as pros from '../api/pros';
import { apiUrls } from '../api';
import { actionTypes } from './index';
import { USER_AUTH } from '../utils/consts';
import { addNotification } from './api-notifications';
import { signInFetchUserData } from './sign-in';

const verifyFailure = () => ({
  type: actionTypes.VERIFY_FAILURE,
});

const loggedOff = () => ({
  type: actionTypes.LOGGED_OFF,
});

const photoUploaded = (photo) => ({
  type: actionTypes.PHOTO_UPLOADED,
  photo,
});

const editSuccess = (response, userAuth) => ({
  type: actionTypes.EDIT_SUCCESS,
  userData: response,
  userAuth,
});
const editFailure = () => ({
  type: actionTypes.EDIT_FAILURE,
});

const registerFailure = (message) => ({
  type: actionTypes.REGISTER_FAILURE,
  message,
});

const resetSuccess = () => ({
  type: actionTypes.RESET_SUCCESS,
});
const resetFailure = (message) => ({
  type: actionTypes.RESET_FAILURE,
  message,
});

const proCallsReceived = (response) => ({
  type: actionTypes.PRO_CALLS_RECEIVED,
  calls: response,
});

const personalCallsReceived = (response) => ({
  type: actionTypes.PERSONAL_CALLS_RECEIVED,
  calls: response,
});

const transactionsReceived = (response) => ({
  type: actionTypes.TRANSACTIONS_RECEIVED,
  payload: response,
});

const withdrawReceived = (response) => ({
  type: actionTypes.WITHDRAW_RECEIVED,
  payload: response,
});

const setCategories = (response) => ({
  type: actionTypes.SET_CATEGORIES,
  categories: response,
});

const sendContactMessageFailure = (message) => ({
  type: actionTypes.SEND_CONTACT_MESS_FAILURE,
  message,
});

const sendContactMessage = () => ({
  type: actionTypes.SEND_CONTACT_MESS,
});

const changePendingActivation = (payload) => ({
  type: actionTypes.REGISTRATION_PENDING_ACTIVATION,
  payload,
});

const translatePayoutv3ResponseToWithdraw = (payload) => ({
  availableAmount: payload.availableAmount,
  totalAmount: payload.totalAmount,
  verificationStatus: payload.verificationStatus,
});

export const changeLocale = (lang) => (dispatch) => {
  dispatch({
    type: actionTypes.CHANGE_LOCALE,
    lang,
  });
};

export const changeLocaleLangs = (langs = []) => (dispatch) => {
  dispatch({
    type: actionTypes.CHANGE_LOCALE_LANGS,
    langs: langs.map((el) => el.lang || el),
  });
};

export const resetPassword = (email, password, code) => async (dispatch) => {
  try {
    await user.resetPassword({ email, password, code });
    dispatch(resetSuccess());
  } catch (err) {
    dispatch(resetFailure(err.message));
  }
};

export const logOff = () => (dispatch, getState) => {
  const { loggedInUser: { userAuth } } = getState();
  user.logOut(userAuth);
  dispatch(loggedOff());
  localStorage.removeItem(USER_AUTH);
  // Sentry.configureScope(((scope) => {
  //  scope.setUser({});
  // }));
  Bugfender.removeDeviceKey('user_email');
  Bugfender.setDeviceKey('user_logged_in', false);
};
export const register = (data) => async (dispatch) => {
  const { onError, ...params } = data;

  try {
    const deviceId = uuidv4();
    localStorage.setItem('uuid', deviceId);

    await user.register({ ...params, device: deviceId, locale: navigator.languages[0] });

    dispatch(changePendingActivation(true));
  } catch ({ message }) {
    if (onError) {
      onError({ message });
    }

    dispatch(registerFailure(message));
  }
};

export const getCategories = (authData) => async (dispatch) => {
  try {
    const response = await user.getCategories(authData);
    dispatch(setCategories(response));
  } catch (err) {
    dispatch(addNotification(err));
  }
};

export const verify = (token) => async (dispatch) => {
  try {
    const response = await user.verify(token);
    dispatch(verifyFailure(response));
  } catch (err) {
    dispatch(addNotification(err));
    dispatch(verifyFailure());
  }
};

export const editDetails = (data, userAuth) => async (dispatch) => {
  try {
    const response = await user.editDetails(data, userAuth);
    dispatch(editSuccess(response, userAuth));
  } catch (err) {
    dispatch(addNotification(err));
    dispatch(editFailure());
  }
};
export const switchEmailNotif = (data, userAuth) => async (dispatch) => {
  try {
    const response = await user.switchData(apiUrls.EMAIL_NOTIFICATIONS, data, userAuth);
    dispatch(editSuccess(response, userAuth));
  } catch (err) {
    dispatch(addNotification(err));
    dispatch(editFailure());
  }
};
export const switchWorkingPro = (data, userAuth) => async (dispatch) => {
  try {
    const response = await user.switchData(apiUrls.WORKING_PRO, data, userAuth);
    dispatch(editSuccess(response, userAuth));
  } catch (err) {
    dispatch(addNotification(err));
    dispatch(editFailure());
  }
};

export const getProCalls = (authData, num) => async (dispatch) => {
  dispatch(proCallsReceived([]));
  try {
    const response = await user.getCalls(authData, true, num);
    dispatch(proCallsReceived(response.results));
  } catch (err) {
    dispatch(addNotification(err));
  }
};

export const getPersonalCalls = (authData, num) => async (dispatch) => {
  dispatch(personalCallsReceived([]));
  try {
    const response = await user.getCalls(authData, false, num);
    dispatch(personalCallsReceived(response.results));
  } catch (err) {
    dispatch(addNotification(err));
  }
};

export const getTransactions = (authData, num, page = 1) => async (dispatch) => {
  dispatch({
    type: actionTypes.GET_TRANSACTIONS,
  });
  try {
    const response = await user.getTransactions(authData, num, page).catch((error) => { throw error; });
    dispatch(transactionsReceived(response));
  } catch (error) {
    dispatch(addNotification(error));
    dispatch({
      type: actionTypes.GET_TRANSACTIONS_FAILURE,
      error,
    });
  }
};

export const getWithdraw = (authData) => async (dispatch) => {
  dispatch({
    type: actionTypes.GET_WITHDRAW,
  });
  try {
    const response = await user.getWithdraw(authData).catch((error) => { throw error; });
    if (Object.keys(response).length !== 0) {
      dispatch(withdrawReceived(translatePayoutv3ResponseToWithdraw(response)));
    }
  } catch (error) {
    dispatch(addNotification(error));
    dispatch({
      type: actionTypes.GET_WITHDRAW_FAILURE,
      error,
    });
  }
};

export const resetTransactions = () => (dispatch) => {
  dispatch({
    type: actionTypes.RESET_TRANSACTIONS,
  });
};

export const uploadPhoto = (authData, photo, filName) => async (dispatch) => {
  try {
    const response = await user.uploadPhoto(authData, photo, filName);
    dispatch(photoUploaded(response));
  } catch (err) {
    dispatch({
      type: actionTypes.PHOTO_UPLOAD_FAILURE,
      errorMsg: err.message || 'Upload photo error!',
    });
  }
};

export const clearuploadPhotoStatus = () => (dispatch) => dispatch({
  type: actionTypes.PHOTO_UPLOAD_CLEAR_STATUS,
});

export const sendContactData = (data) => async (dispatch) => {
  dispatch(sendContactMessage());
  try {
    const response = await user.sendContactData(data);
    dispatch(sendContactMessageFailure(response.message));
  } catch (err) {
    dispatch(addNotification(err));
    dispatch(sendContactMessageFailure(err.message));
  }
};

export const clearContactData = () => (dispatch) => dispatch(sendContactMessage());

export const getUserDetails = (id, isPro) => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.GET_USER_DETAILS,
  });
  const { loggedInUser: { userAuth } } = getState();
  try {
    const response = await pros.getUserDetails(id, userAuth, isPro).catch((err) => { throw err; });
    if (response.pro) {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const availabilities = await pros.getProAvailabilityForTimezone(id, timezone, userAuth);
      response.pro.availability = {
        total: availabilities.length,
        results: availabilities,
      };
    }
    dispatch({
      type: actionTypes.GET_USER_DETAILS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch(addNotification(error));
    dispatch({
      type: actionTypes.GET_USER_DETAILS_FAILURE,
      error,
    });
  }
};

export const refreshLoggedInUserDetails = () => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.REFRESH_LOGGED_IN_USER_DETAILS,
  });

  const { loggedInUser: { userAuth } } = getState();

  try {
    const response = await user.getUserInfo(userAuth).catch((err) => { throw err; });
    dispatch({
      type: actionTypes.REFRESH_LOGGED_IN_USER_DETAILS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch(addNotification(error));
    dispatch({
      type: actionTypes.REFRESH_LOGGED_IN_USER_DETAILS_FAILURE,
      error,
    });
  }
};

export const getUserByRef = (id) => async (dispatch) => {
  dispatch({
    type: actionTypes.GET_USER_DETAILS,
  });
  try {
    const response = await pros.getUserByRef(id).catch((err) => { throw err; });
    dispatch({
      type: actionTypes.GET_USER_DETAILS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch(addNotification(error));
    dispatch({
      type: actionTypes.GET_USER_DETAILS_FAILURE,
      error,
    });
  }
};

export const resetUserDetails = () => (dispatch) => {
  dispatch({
    type: actionTypes.RESET_USER_DETAILS,
  });
};

export const setUserState = (key) => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.SET_STATE,
  });
  try {
    const { loggedInUser: { userAuth } } = getState();
    const response = await user.setUserState(key, userAuth).catch((err) => { throw err; });
    dispatch({
      type: actionTypes.SET_STATE_SUCCESS,
      payload: response,
    });
    if (key === 'showFullName') dispatch(signInFetchUserData(userAuth));
  } catch (err) {
    dispatch(addNotification(err));
    dispatch({
      type: actionTypes.SET_STATE_FAILURE,
      error: err,
    });
  }
};

export const removeUserState = (key) => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.SET_STATE,
  });
  try {
    const { loggedInUser: { userAuth } } = getState();
    const response = await user.removeUserState(key, userAuth).catch((err) => { throw err; });
    dispatch({
      type: actionTypes.SET_STATE_SUCCESS,
      payload: response,
    });
    if (key === 'showFullName') dispatch(signInFetchUserData(userAuth));
  } catch (err) {
    dispatch(addNotification(err));
    dispatch({
      type: actionTypes.SET_STATE_FAILURE,
      error: err,
    });
  }
};

export const getAvailability = () => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.GET_AVAILABILITY,
  });
  try {
    const { loggedInUser: { userAuth } } = getState();
    const response = await user.getAvailability(userAuth).catch((err) => { throw err; });
    dispatch({
      type: actionTypes.GET_AVAILABILITY_SUCCESS,
      payload: response,
    });
  } catch (err) {
    dispatch(addNotification(err));
    dispatch({
      type: actionTypes.GET_AVAILABILITY_FAILURE,
      error: err,
    });
  }
};

export const setAvailability = (data) => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.SET_AVAILABILITY,
  });

  try {
    const { loggedInUser: { userAuth } } = getState();
    const response = await user.setAvailability(userAuth, data).catch((err) => { throw err; });

    dispatch({
      type: actionTypes.SET_AVAILABILITY_SUCCESS,
      payload: response,
    });
  } catch (err) {
    dispatch(addNotification(err));
    dispatch({
      type: actionTypes.SET_AVAILABILITY_FAILURE,
      error: err,
    });
  }
};

export const updateAvailability = (data) => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.UPDATE_AVAILABILITY,
  });
  try {
    const { loggedInUser: { userAuth } } = getState();
    const response = await user.updateAvailability(userAuth, data).catch((err) => { throw err; });
    dispatch({
      type: actionTypes.UPDATE_AVAILABILITY_SUCCESS,
      payload: response,
    });
  } catch (err) {
    dispatch(addNotification(err));
    dispatch({
      type: actionTypes.UPDATE_AVAILABILITY_FAILURE,
      error: err,
    });
  }
};

export const deleteAvailability = (id) => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.DELETE_AVAILABILITY,
  });
  try {
    const { loggedInUser: { userAuth } } = getState();
    const response = await user.deleteAvailability(userAuth, id).catch((err) => { throw err; });
    dispatch({
      type: actionTypes.DELETE_AVAILABILITY_SUCCESS,
      payload: response,
    });
  } catch (err) {
    dispatch(addNotification(err));
    dispatch({
      type: actionTypes.DELETE_AVAILABILITY_FAILURE,
      error: err,
    });
  }
};

export const updateUserSettings = (data) => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.UPDATE_USER_SETTINGS,
  });
  try {
    const { loggedInUser: { userAuth } } = getState();
    const response = await user.updateUserSettings(userAuth, data).catch((err) => { throw err; });
    dispatch({
      type: actionTypes.UPDATE_USER_SETTINGS_SUCCESS,
      payload: response,
    });
  } catch (err) {
    dispatch(addNotification(err));
    dispatch({
      type: actionTypes.UPDATE_AVAILABILITY_FAILURE,
      error: err,
    });
  }
};

export const setBranchIoData = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.BRANCH_IO_DATA,
    payload: data,
  });
};

export const getDeepLink = () => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.GET_DEEP_LINK,
  });
  try {
    const { loggedInUser: { userAuth } } = getState();
    const response = await user.getDeepLink(userAuth);
    dispatch({
      type: actionTypes.GET_DEEP_LINK_SUCCESS,
      payload: response,
    });
  } catch (err) {
    dispatch(addNotification(err));
    dispatch({
      type: actionTypes.GET_DEEP_LINK_FAILURE,
      error: err,
    });
  }
};

export const sendGlobalMessage = (message) => (dispatch) => {
  dispatch({
    type: actionTypes.GLOBAL_MESSAGE,
    payload: message,
  });
};

export const getUnreadMessages = () => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.GET_BADGE,
  });
  try {
    const { loggedInUser: { userAuth } } = getState();
    const response = await user.unreadMessagesController(userAuth);
    dispatch({
      type: actionTypes.GET_BADGE_SUCCESS,
      payload: response,
    });
  } catch (err) {
    dispatch(addNotification(err));
    dispatch({
      type: actionTypes.GET_BADGE_FAILURE,
      error: err,
    });
  }
};

export const updateBadges = (person) => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.UPDATE_BADGE,
  });
  // eslint-disable-next-line no-console
  console.log('PERSON', person);

  try {
    const { loggedInUser: { userAuth } } = getState();

    await user.unreadMessagesController(userAuth, person.toUser && person.toUser.id);
  } catch (err) {
    dispatch(addNotification(err));
    dispatch({
      type: actionTypes.UPDATE_BADGE_FAILURE,
      error: err,
    });
  }
};

export const setRediretLink = (path) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_REDIRECT_LINK,
    payload: path,
  });
};

export function forgotPasswordReset() {
  return {
    type: actionTypes.FORGOT_PASSWORD_RESET,
  };
}



// WEBPACK FOOTER //
// ./actions/user.js